/**
 * Shared formatting utilities for date/time display and stat rendering.
 * All game times are displayed in Eastern Time (ET) per hard UI requirements.
 */

const ET_TIMEZONE = "America/New_York";

function normalizeTimeZoneName(timeZoneName: string | null | undefined): string | null {
  if (!timeZoneName || typeof timeZoneName !== "string") {
    return null;
  }

  const normalized = timeZoneName.trim();
  return normalized || null;
}

function formatClockTimeWithTimeZone(parsed: Date, timeZoneName: string): string | null {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timeZoneName,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    }).format(parsed);
  } catch {
    return null;
  }
}

/**
 * Format a UTC ISO-8601 game start time into a human-readable ET string.
 * Example: "Mar 2, 2026 • 7:10 PM ET"
 */
export function formatGameDateTimeEt(gameDate: string, gameStartTimeUtc: string | null): string {
  if (!gameStartTimeUtc) {
    return `${gameDate} • Time TBD`;
  }

  const parsed = new Date(gameStartTimeUtc);
  if (Number.isNaN(parsed.getTime())) {
    return `${gameDate} • Time TBD`;
  }

  const etDate = new Intl.DateTimeFormat("en-US", {
    timeZone: ET_TIMEZONE,
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(parsed);

  const etTime = new Intl.DateTimeFormat("en-US", {
    timeZone: ET_TIMEZONE,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(parsed);

  return `${etDate} • ${etTime} ET`;
}

export function formatGameLocalTime(
  gameStartTimeUtc: string | null,
  venueTimeZone: string | null | undefined,
): string | null {
  const normalizedTimeZone = normalizeTimeZoneName(venueTimeZone);
  if (!gameStartTimeUtc || !normalizedTimeZone) {
    return null;
  }

  if (normalizedTimeZone.toLowerCase() === ET_TIMEZONE.toLowerCase()) {
    return null;
  }

  const parsed = new Date(gameStartTimeUtc);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const localTime = formatClockTimeWithTimeZone(parsed, normalizedTimeZone);
  return localTime ? `Local: ${localTime}` : null;
}

/**
 * Format a "last updated at" UTC timestamp for display.
 */
export function formatLastUpdatedEt(lastUpdatedAt: string | null): string {
  if (!lastUpdatedAt) {
    return "Unavailable";
  }

  const parsed = new Date(lastUpdatedAt);
  if (Number.isNaN(parsed.getTime())) {
    return "Unavailable";
  }

  return new Intl.DateTimeFormat("en-US", {
    timeZone: ET_TIMEZONE,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(parsed);
}

/**
 * Format a relative time string (e.g., "3 minutes ago", "1 hour ago").
 * Returns null if the timestamp is invalid.
 */
export function formatRelativeTime(utcTimestamp: string | null): string | null {
  if (!utcTimestamp) return null;
  const parsed = new Date(utcTimestamp).getTime();
  if (Number.isNaN(parsed)) return null;

  const diffMs = Date.now() - parsed;
  const diffMin = Math.round(diffMs / 60_000);

  if (diffMin < 1) return "just now";
  if (diffMin === 1) return "1 minute ago";
  if (diffMin < 60) return `${diffMin} minutes ago`;

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr === 1) return "1 hour ago";
  if (diffHr < 24) return `${diffHr} hours ago`;

  return `${Math.floor(diffHr / 24)} days ago`;
}

/**
 * Format a 0-1 confidence float as a user-facing percentage string.
 * Returns "—" for null/NaN values. Rounds to 1 decimal place.
 */
export function formatConfidencePercent(confidence: number | null): string {
  if (confidence === null || Number.isNaN(confidence)) {
    return "—";
  }
  return `${(confidence * 100).toFixed(1)}%`;
}

/**
 * Format a batting average / rate stat to 3 decimal places (e.g., .311).
 * Returns "—" for null.
 */
export function formatBattingRate(value: number | null): string {
  if (value === null || Number.isNaN(value)) {
    return "—";
  }
  return value.toFixed(3);
}

/**
 * Format a counting stat (integers). Returns "—" for null.
 */
export function formatCountingStat(value: number | null): string {
  if (value === null || Number.isNaN(value)) {
    return "—";
  }
  return String(Math.round(value));
}

/**
 * Format a pitching rate stat (ERA, WHIP) to 2 decimal places.
 * Returns "—" for null.
 */
export function formatPitchingRate(value: number | null): string {
  if (value === null || Number.isNaN(value)) {
    return "—";
  }
  return value.toFixed(2);
}

/**
 * Format innings pitched (can be fractional like 82.1). Shows 1 decimal.
 */
export function formatInningsPitched(value: number | null): string {
  if (value === null || Number.isNaN(value)) {
    return "—";
  }
  return value.toFixed(1);
}
